import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { DEFAULT_RETRY } from '../../api/queryDefaults'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { isCreditCard } from './accountGroups'
import { statementErrorKind, toStatementCycles, type StatementCycles } from './statementCyclesWire'
import { AccountsError, type Account } from './useAccounts'

/** The current cycle plus five previous, from ONE response — no "show more". */
export const CYCLE_COUNT = 6

export type CyclesResult =
  | { kind: 'cycles'; data: StatementCycles }
  | { kind: 'not_set' }
  | { kind: 'hidden' }

/**
 * The viewer's IANA zone, `UTC` when the runtime cannot say. Wrong by at most a
 * day on a cycle boundary beats an error card (same read as `Cashflow.tsx`).
 */
export function browserTimeZone(): string {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Zones the server answered 422 `tz:` for, this session. */
const REJECTED_ZONES = new Set<string>()

/** Test seam: the set outlives a component, so a test clears it. */
export function __resetRejectedZones(): void {
  REJECTED_ZONES.clear()
}

async function fetchCycles(accountId: string, tz: string): Promise<CyclesResult> {
  const { data, error, response } = await api.GET('/api/v1/accounts/{account_id}/statement-cycles', {
    params: { path: { account_id: accountId }, query: { tz, count: CYCLE_COUNT } },
  })
  if (data !== undefined && error === undefined) return { kind: 'cycles', data: toStatementCycles(data) }
  if (response.status === 404) return { kind: 'hidden' }
  if (response.status === 422) {
    switch (statementErrorKind(error)) {
      case 'not_credit':
        return { kind: 'hidden' }
      case 'not_set':
        return { kind: 'not_set' }
      case 'tz':
        // The server does not know this zone: ONE retry in UTC, never a loop,
        // and remembered so the next visit asks for UTC straight away.
        if (tz !== 'UTC') {
          REJECTED_ZONES.add(tz)
          return fetchCycles(accountId, 'UTC')
        }
        break
      default:
        break
    }
  }
  throw new AccountsError(error, response.status)
}

/**
 * `GET /accounts/{id}/statement-cycles` for the "This cycle" card.
 *
 * `visible` is the backend's own credit rule (`isCreditCard`); the request is
 * made only when the day is also known to be set — a not-set account would
 * 422, so the card reads `account.statementDay === null` and renders the empty
 * state with no call. A 404 or a not-credit 422 means "render nothing"
 * (`hidden`). The `statement-cycles` surface is `live`: the response has no
 * `last_synced_at`, so no `asOf`; the page's `accounts` surface owns staleness.
 *
 * The key sits under the `transactions` root, so any transaction edit
 * refreshes the cycles.
 */
export function useStatementCycles(account: Account): {
  visible: boolean
  state: AsyncState<CyclesResult>
} {
  const visible = isCreditCard(account.type)
  const enabled = visible && account.statementDay !== null
  const [tz] = useState(() => {
    const zone = browserTimeZone()
    return REJECTED_ZONES.has(zone) ? 'UTC' : zone
  })
  const query = useQuery({
    queryKey: keys.transactions.statementCycles(account.id, tz, CYCLE_COUNT),
    queryFn: () => fetchCycles(account.id, tz),
    enabled,
    // A 422 is deterministic (fetchCycles already tried UTC): replaying the
    // whole attempt only repeats it. Anything else keeps the app default.
    retry: (failures, e) => !(e instanceof AccountsError && e.status === 422) && failures < DEFAULT_RETRY,
  })
  const state = fromQuery<CyclesResult>(query, {
    message: () => 'Statement cycles could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (r) => r.kind === 'not_set',
  })
  // Disabled queries are `pending` forever; the not-set state is known
  // locally, so it is synthesised rather than left loading.
  return { visible, state: enabled ? state : { status: 'empty' } }
}
